import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { NavbarStateService } from 'app/student/services/navbar-state.service';

@UntilDestroy()
@Component({
  selector: 'kh-scroll-indicator',
  templateUrl: './scroll-indicator.component.html',
  styleUrls: ['./scroll-indicator.component.less'],
  standalone: false
})
export class ScrollIndicatorComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navbarStateService = inject(NavbarStateService, { optional: true });

  @Input() public text = 'SCROLL FOR MORE';
  @Input() public targetItemSelector = '.scrollable-item'; // Selector for items to observe
  @Input() public iconSrc = 'https://staticcontent.knowledgehook.com/content/khg/ui/garden-mission/scrolldown.png';

  public isVisible = false;
  public indicatorLeftPosition = 0;
  public showText = true;

  private readonly EDGE_TOLERANCE = -20;
  private readonly INDICATOR_WIDTH = 160;
  private readonly SPACING = 20;
  private readonly NAVBAR_HEIGHT = 64; // @bottom-nav-bar-height from LESS variables
  private readonly NAVBAR_COLLAPSED_HEIGHT = 0; // When collapsed, navbar takes no space
  private readonly BOTTOM_MARGIN = 6; // Additional margin for better positioning

  private _resizeListener?: () => void;
  private _scrollListener?: () => void;
  private _resizeDebounceTimer?: number;
  private _scrollDebounceTimer?: number;
  private _initRetryTimer?: number;
  private _positionRetryTimer?: number;
  private _isNavbarCollapsed = false;

  public ngOnInit(): void {
    this.subscribeToNavbarState();
    this.setupEventListeners();
  }

  public ngAfterViewInit(): void {
    // DOM is now fully rendered, safe to initialize
    this.initializeAfterViewInit();
  }

  public ngOnDestroy(): void {
    this.cleanup();
  }

  private setupEventListeners(): void {
    // Setup scroll listener
    this._scrollListener = this.handleScroll.bind(this);
    window.addEventListener('scroll', this._scrollListener, { passive: true });

    // Setup resize listener
    this._resizeListener = this.handleResize.bind(this);
    window.addEventListener('resize', this._resizeListener, { passive: true });
  }

  private initializeAfterViewInit(): void {
    // For component's own DOM elements, they should be ready immediately after ngAfterViewInit
    // For external target elements (from parent components), we might still need to wait
    const targetItems = document.querySelectorAll(this.targetItemSelector);

    if (targetItems.length > 0) {
      // Target elements are available, proceed with initialization
      this.updateIndicatorPosition();
      this.checkVisibilityManually();
    }
    else {
      // External target elements might not be ready yet, use retry mechanism
      this.waitForTargetElements();
    }
  }

  private waitForTargetElements(): void {
    const maxRetries = 10;
    let retryCount = 0;

    const tryInitialize = () => {
      const targetItems = document.querySelectorAll(this.targetItemSelector);

      if (targetItems.length > 0) {
        // Elements are available, proceed with initialization
        this.updateIndicatorPosition();
        this.checkVisibilityManually();
      }
      else if (retryCount < maxRetries) {
        // Elements not ready yet, retry after a short delay
        retryCount++;
        this._initRetryTimer = window.setTimeout(tryInitialize, 100);
      }
      // If max retries reached, give up (elements might not exist on this page)
    };

    tryInitialize();
  }

  private subscribeToNavbarState(): void {
    if (this.navbarStateService) {
      this.navbarStateService.isCollapsed$
        .pipe(untilDestroyed(this))
        .subscribe((isCollapsed) => {
          this._isNavbarCollapsed = isCollapsed;
          // Recalculate visibility when navbar state changes
          this.checkVisibilityManually();
        });
    }
  }

  private handleScroll(): void {
    // Clear previous timeout to properly debounce scroll events
    clearTimeout(this._scrollDebounceTimer);
    this._scrollDebounceTimer = window.setTimeout(() => {
      this.checkVisibilityManually();
    }, 50); // Reduced debounce for better responsiveness
  }

  private handleResize(): void {
    // Clear previous timeout to properly debounce resize events
    clearTimeout(this._resizeDebounceTimer);
    this._resizeDebounceTimer = window.setTimeout(() => {
      this.updateIndicatorPosition();
      this.checkVisibilityManually();
    }, 100);
  }

  private updateIndicatorPosition(): void {
    const firstTargetItem = document.querySelector(this.targetItemSelector);
    if (firstTargetItem) {
      const rect = firstTargetItem.getBoundingClientRect();

      // Only proceed if element has valid dimensions (is fully rendered)
      if (rect.width === 0 && rect.height === 0) {
        // Element not ready yet, retry after a short delay
        this._positionRetryTimer = window.setTimeout(() => {
          this.updateIndicatorPosition();
        }, 50);
        return;
      }

      const missionsContainerLeft = rect.left + window.scrollX;
      const calculatedPosition = missionsContainerLeft - this.INDICATOR_WIDTH - this.SPACING;

      const oldShowText = this.showText;
      const oldPosition = this.indicatorLeftPosition;

      if (calculatedPosition < this.EDGE_TOLERANCE) {
        this.showText = false;
        this.indicatorLeftPosition = this.EDGE_TOLERANCE; // Set to edge tolerance to avoid going off the screen
      }
      else {
        this.showText = true;
        this.indicatorLeftPosition = calculatedPosition;
      }

      // Trigger change detection if any property changed
      if (oldShowText !== this.showText || oldPosition !== this.indicatorLeftPosition) {
        this.cdr.detectChanges();
      }
    }
  }

  private checkVisibilityManually(): void {
    const targetItems = document.querySelectorAll(this.targetItemSelector);
    if (targetItems.length === 0) {
      this.setVisibility(false);
      return;
    }

    const lastItem = targetItems[targetItems.length - 1];
    const rect = lastItem.getBoundingClientRect();

    // Only proceed if element has valid dimensions
    if (rect.width === 0 && rect.height === 0) {
      return; // Element not ready yet
    }

    const navbarHeight = this._isNavbarCollapsed ? this.NAVBAR_COLLAPSED_HEIGHT : this.NAVBAR_HEIGHT;
    const viewportHeight = window.innerHeight - navbarHeight - this.BOTTOM_MARGIN;

    // Show indicator only if there's content below the viewport
    const shouldBeVisible = rect.bottom > viewportHeight;
    this.setVisibility(shouldBeVisible);
  }

  private setVisibility(visible: boolean): void {
    if (this.isVisible !== visible) {
      this.isVisible = visible;
      this.cdr.detectChanges();
    }
  }

  private cleanup(): void {
    // Remove event listeners
    if (this._resizeListener) {
      window.removeEventListener('resize', this._resizeListener);
      this._resizeListener = undefined;
    }

    if (this._scrollListener) {
      window.removeEventListener('scroll', this._scrollListener);
      this._scrollListener = undefined;
    }

    // Clear any pending timers
    clearTimeout(this._resizeDebounceTimer);
    clearTimeout(this._scrollDebounceTimer);
    clearTimeout(this._initRetryTimer);
    clearTimeout(this._positionRetryTimer);
  }
}
